/* eslint-disable import/max-dependencies */

import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import SuspenseSpinner from '../components/SuspenseFallback'
import { useApplicationContext } from '../context/ApplicationContext'
import { getCurrentEnvironment } from '../utils/session-utils'
import { UserUnauthorized } from '../components/common/user-unauthorized'
import DeprecatedRoutes from '../utils/DeprecatedRoutes'
import { ELMO_EXPERIMENTS } from '../constants'
import { cleanupOnLogin } from '../utils/storage-util'
import { TrackEventProvider } from '../components/upgrade-hub/analytics/track-event-context'
import UpgradeGuideProvider from '../pages/upgrade-your-apps/upgrade-guide/utils/upgrade-guide-util'

const Account = lazy(() => import('../pages/account'))
const TestCards = lazy(() => import('../pages/account/test-cards'))
const ErrorLogFailures = lazy(() => import('../pages/api-webhooks-errors'))
const WebhooksEvents = lazy(() => import('../pages/webhooks-events'))
const APICalls = lazy(() => import('../pages/dashboard'))
const Help = lazy(() => import('../pages/help'))
const DashboardHome = lazy(() => import('../pages/home'))
const Applications = lazy(() => import('../pages/applications'))
const CreateApplication = lazy(() => import('../pages/applications/create'))
const ApplicationDetails = lazy(() => import('../pages/applications/edit'))
const IpnSimulator = lazy(() => import('../pages/ipn'))
const NotFound = lazy(() => import('../pages/notfound'))
const BTCredDetailsLive = lazy(() =>
  import('../pages/applications/editbtcred/editbtcredLive'),
)
const BTCredDetailsSandbox = lazy(() =>
  import('../pages/applications/editbtcred/editbtcredSandbox'),
)
const CreditCardGenerator = lazy(() =>
  import('../pages/mock/creditCardGenerator'),
)
const WebhookSimulator = lazy(() => import('../pages/mock/webhookSimulator'))
const SandboxAccount = lazy(() => import('../pages/sandbox-account'))
const SandboxAccountDetails = lazy(() =>
  import('../pages/sandbox-account/edit'),
)
const SandboxCreateAccount = lazy(() =>
  import('../pages/sandbox-account/create'),
)
const SandboxCreateCustomAccount = lazy(() =>
  import('../pages/sandbox-account/create-custom'),
)
const SandboxBulkCreateA = lazy(() => import('../pages/sandbox-account/bulk'))
const Notifications = lazy(() => import('../pages/notifications'))
const Transactions = lazy(() => import('../pages/transactions'))
const UpgradeYourApps = lazy(() => import('../pages/upgrade-your-apps'))
const UpgradeGuidePage = lazy(() =>
  import('../pages/upgrade-your-apps/upgrade-guide'),
)

const AnalyticsProvider = lazy(() =>
  // eslint-disable-next-line import/max-dependencies
  import('../components/upgrade-hub/analytics/analytics-context'),
)

function SignInPage() {
  const { user, contextualLoginUrl } = useApplicationContext()
  if (user) {
    if (user.unauthorized === true) {
      cleanupOnLogin()
      return <UserUnauthorized />
    }
    return <Outlet />
  }
  cleanupOnLogin()
  window.location.href = contextualLoginUrl
  return null
}

function AuthRequired() {
  return (
    <>
      <SignInPage />
    </>
  )
}

function UpgradeHubExperience() {
  const { upgradehub_v2 } = useApplicationContext()
  if (upgradehub_v2 === ELMO_EXPERIMENTS.UpgradeHub.Treatment) {
    return (
      <AnalyticsProvider>
        <TrackEventProvider trackScrolled>
          <link
            rel="prefetch"
            as="style"
            href="https://www.paypalobjects.com/paypal-ui/web/fonts-and-normalize/3-0-0/fonts-and-normalize.min.css"
          />
          <Outlet />
        </TrackEventProvider>
      </AnalyticsProvider>
    )
  }
  return <NotFound />
}

const AppRoutes = () => {
  const { user } = useApplicationContext()
  return (
    <Suspense fallback={<SuspenseSpinner />}>
      <Routes>
        <Route path="/" element={<AuthRequired />}>
          <Route exact path="/" element={<DashboardHome />} />

          <Route
            exact
            path="/applications"
            element={
              <Navigate
                to={'/applications/' + getCurrentEnvironment(user)}
                replace
              />
            }
          />
          <Route
            element={
              <DeprecatedRoutes
                newPath="/applications/sandbox"
                params={{ openBTCredModal: true }}
              />
            }
          >
            <Route path="/applications/createbtcred" element={<></>} />
          </Route>
          <Route
            element={
              <DeprecatedRoutes
                newPath="/applications/live"
                params={{ openBTCredModal: true }}
              />
            }
          >
            <Route path="/applications/createbtcredlive" element={<></>} />
          </Route>
          <Route index path="/applications/:env" element={<Applications />} />
          <Route
            path="/applications/edit/:id"
            element={<ApplicationDetails />}
          />
          <Route
            index
            path="/applications/:env/create"
            element={<CreateApplication />}
          />
          <Route
            path="/applications/braintreeCredentials/editbtcredLive"
            element={<BTCredDetailsLive />}
          />
          <Route
            path="/applications/braintreeCredentials/editbtcred"
            element={<BTCredDetailsSandbox />}
          />
          <Route path="/accounts" element={<SandboxAccount />} />
          <Route
            path="/accounts/edit/:id"
            element={<SandboxAccountDetails />}
          />
          <Route path="/accounts/test-cards/:id" element={<TestCards />} />
          <Route element={<DeprecatedRoutes newPath="/accounts" />}>
            <Route path="/accounts/create" element={<SandboxCreateAccount />} />
            <Route
              path="/accounts/create/custom"
              element={<SandboxCreateCustomAccount />}
            />
            <Route path="/accounts/bulk" element={<SandboxBulkCreateA />} />
          </Route>
          <Route
            path="/creditCardGenerator"
            element={<CreditCardGenerator />}
          />
          <Route
            path="/api-calls/sandbox"
            element={<APICalls environment={'sandbox'} />}
          />
          <Route
            path="/api-calls/live"
            element={<APICalls environment={'live'} />}
          />
          <Route
            path="/webhooks/live"
            element={<WebhooksEvents environment={'live'} />}
          />
          <Route
            path="/webhooks/sandbox"
            element={<WebhooksEvents environment={'sandbox'} />}
          />
          <Route path="/webhooksSimulator" element={<WebhookSimulator />} />
          <Route path="/ipnSimulator" element={<IpnSimulator />} />
          <Route path="/notifications/" element={<Notifications />} />
          <Route
            path="/errors/sandbox"
            element={<ErrorLogFailures environment={'sandbox'} />}
          />
          <Route
            path="/errors/live"
            element={<ErrorLogFailures environment={'live'} />}
          />
          <Route
            path="/notifications/recent/:accountid"
            element={<Notifications />}
          />
          <Route path="/accountStatus" element={<Account />} />
          <Route path="/help" element={<Help />} />
          <Route path="/transactions/:env" element={<Transactions />} />
          <Route path="/upgrade-your-apps" element={<UpgradeHubExperience />}>
            <Route index element={<UpgradeYourApps />} />
            <Route
              path="upgrade-guide/:appName"
              element={
                <UpgradeGuideProvider>
                  <link
                    href="https://www.paypalobjects.com/paypal-ui/web/fonts-and-normalize/3-0-0/fonts-and-normalize.min.css"
                    type="stylesheet"
                  />
                  <UpgradeGuidePage />
                </UpgradeGuideProvider>
              }
            />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default AppRoutes
